import { LineItem, Order, Payment } from 'shared/types/cart';
import { EnumAttribute } from 'shared/types/product';
import { getEnvOrFail } from 'env/env';
import {
  SUPPORTED_LOCAL_CURRENCIES,
  type SupportedLocalCurrency,
} from 'utils/currency';
import { centsToDollarString } from 'utils/price';
import type { CourseDetails, LocalCurrencyCharge } from './types';

/**
 * When the first payment was taken in a supported non-USD currency, return display data from `amountPlanned`.
 */
export const getLocalCurrencyCharge = (
  order: Order,
): LocalCurrencyCharge | null => {
  const payment = order.payments?.[0];
  if (!payment) {
    return null;
  }

  const planned = payment.amountPlanned;
  if (planned?.currencyCode == null || planned.centAmount == null) {
    return null;
  }

  const code = planned.currencyCode.toUpperCase();
  if (
    code === 'USD' ||
    !SUPPORTED_LOCAL_CURRENCIES.includes(code as SupportedLocalCurrency)
  ) {
    return null;
  }

  const fractionDigits = planned.fractionDigits ?? 2;
  const amount = planned.centAmount / 10 ** fractionDigits;

  const formattedAmount = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: code,
    minimumFractionDigits: fractionDigits,
    maximumFractionDigits: fractionDigits,
  }).format(amount);

  return {
    currencyCode: code as SupportedLocalCurrency,
    formattedAmount,
  };
};

export const getCoursesDetails = (
  lineItems: Required<LineItem>[],
): CourseDetails[] =>
  lineItems.map(
    ({ name, price, variant: { sku, images, attributes = {} } }) => ({
      name,
      sku,
      url: `${getEnvOrFail('NEXT_PUBLIC_LEARNING_BASE_URL')}/course/${
        attributes['external-ids-variant'] ?? sku
      }/home`,
      image: images?.[0],
      price: centsToDollarString(price.centAmount, price.fractionDigits),
      partner: attributes['brand-text'],
      startDate: attributes['courserun-start']!,
      duration: {
        low: attributes['duration-low'],
        high: attributes['duration-high'],
        unit: attributes['duration-unit']?.label,
      },
    }),
  );

/**
 * Get the latest payment method from the payments array in a human-readable format
 */
export const getPaymentMethod = (payments: Payment[]) => {
  const paymentMethod = payments?.at(-1)?.paymentMethod ?? 'card';
  return paymentMethod === 'paypal'
    ? 'PayPal'
    : paymentMethod.replace(/_/g, ' ');
};

export const formatProgramType = (type: EnumAttribute) => {
  if (!type) {
    return 'Program';
  }

  const { label, key } = type;

  switch (key) {
    case 'micromasters':
    case 'microbachelors':
      return (
        <span>
          &nbsp;
          {label}
          <sup>&reg;</sup> Program
        </span>
      );
    case 'masters':
      return <span>&nbsp;Master&apos;s</span>;
    case 'xseries':
      return `${label} Program`;
    default:
      return label;
  }
};
